import { useEffect } from 'react';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';

/**
 * Hook qui enrichit le store avec tous les shifts du team connecté.
 * S'exécute APRÈS le sync principal pour récupérer les shifts qui auraient été perdus
 * à cause des RLS ou des filtres de projet.
 */
export function useTeamShiftEnricher() {
  const { currentUser } = useStore();

  useEffect(() => {
    const teamId = currentUser?.teamId;
    if (!teamId || currentUser?.role !== 'staff') return;

    let cancelled = false;

    async function enrich() {
      await new Promise(r => setTimeout(r, 2000));

      if (cancelled) return;

      // 1. Récupérer TOUS les shifts du team
      const { data: shifts, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('staff_id', teamId)
        .is('deleted_at', null);

      if (cancelled || error || !shifts || shifts.length === 0) return;

      const mappedShifts = shifts.map((row: any) => ({
        id: row.id,
        teamId: row.staff_id,
        pointId: row.point_id,
        orgId: row.org_id,
        projectId: row.project_id,
        start: row.start_time.substring(0, 16),
        end: row.end_time.substring(0, 16),
        description: row.description,
        actualStart: row.actual_start ?? undefined,
        actualEnd: row.actual_end ?? undefined,
        shiftType: row.shift_type ?? 'standard',
        proposedAmount: row.proposed_amount,
        agreedAmount: row.agreed_amount,
        amountNegotiatedByTeam: row.amount_negotiated_by_staff ?? false,
        proposedCurrency: row.proposed_currency ?? 'TND',
        agreementStatus: row.agreement_status ?? 'approved',
      }));

      // 2. Récupérer les org_id des shifts
      const teamOrgIds = [...new Set(mappedShifts.map(s => s.orgId).filter(Boolean))] as string[];

      // 3. Ajouter les shifts manquants
      const store = useStore.getState();
      const existingTeamShiftIds = new Set(
        store.shifts.filter(s => s.teamId === teamId).map(s => s.id)
      );
      const newShifts = mappedShifts.filter(s => !existingTeamShiftIds.has(s.id));

      if (newShifts.length > 0) {
        console.log(`[Enricher] Found ${newShifts.length} missing shifts for team ${teamId}:`, 
          newShifts.map(s => ({ id: s.id, orgId: s.orgId, pointId: s.pointId, start: s.start }))
        );
        useStore.setState({ shifts: [...store.shifts, ...newShifts] });
        console.log(`[Enricher] Added ${newShifts.length} shifts. Total now: ${useStore.getState().shifts.length}`);
      }

      // 4. Ajouter les points manquants des orgs manquantes
      if (teamOrgIds.length > 0) {
        const existingOrgIds = new Set(store.points.map(p => p.orgId));
        const missingOrgIds = teamOrgIds.filter(oid => !existingOrgIds.has(oid));

        if (missingOrgIds.length > 0) {
          console.log(`[Enricher] Missing org points: ${missingOrgIds.join(', ')}. Fetching...`);

          const { data: missingPoints, error: ptsErr } = await supabase
            .from('points')
            .select('*')
            .in('org_id', missingOrgIds)
            .is('deleted_at', null);

          if (!cancelled && !ptsErr && missingPoints && missingPoints.length > 0) {
            const { data: hrs } = await supabase
              .from('point_opening_hours')
              .select('*')
              .in('point_id', missingPoints.map(p => p.id));

            const mappedPoints = missingPoints.map((p: any) => {
              const hours = (hrs || [])
                .filter((h: any) => h.point_id === p.id)
                .map((h: any) => ({
                  start: h.start_time.substring(0, 16),
                  end: h.end_time.substring(0, 16),
                  requiredTeam: h.required_staff,
                  isHard: h.is_hard,
                }));
              return {
                id: p.id,
                name: p.name,
                categoryId: p.category_id,
                orgId: p.org_id,
                projectId: p.project_id,
                iconName: p.icon_name,
                location: p.location_lat ? { lat: p.location_lat, lng: p.location_lng } : undefined,
                requiredTeam: p.required_staff,
                openingHours: hours,
                swapEnabled: p.swap_enabled,
                isMealPoint: p.is_meal_point,
                unavailabilityEnabled: p.unavailability_enabled,
                isMapFocus: p.is_map_focus,
                descriptionMarkdown: p.description_markdown ?? undefined,
                isRestricted: p.is_restricted ?? false,
                isStage: p.is_stage ?? false,
              };
            });

            console.log(`[Enricher] Adding ${mappedPoints.length} missing points from orgs: ${missingOrgIds.join(', ')}`);
            const currentPoints = useStore.getState().points;
            useStore.setState({ points: [...currentPoints, ...mappedPoints] });
          }
        }
      }
    }

    enrich();
    return () => { cancelled = true; };
  }, [currentUser?.teamId, currentUser?.role]);
}

